import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { toaster } from '@/shared/libs';
import { Button, Dialog, DialogFooter, Input, Label } from '@/shared/ui';
import { useCreateDealMutation } from '@entities/Deal';
import { getOfferDisplayName } from '@entities/Offer';
import type { Offer } from '@psp-aggregator/sdk';

export interface MerchantApplyProps {
  offers: readonly Offer[];
  onClose: () => void;
  onCreated: () => void;
}

export const MerchantApply: FC<MerchantApplyProps> = ({
  offers,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const mutation = useCreateDealMutation();
  const [brand, setBrand] = useState('');
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('');

  const submit = async (): Promise<void> => {
    try {
      await mutation.mutateAsync({
        items: offers.map((offer) => ({ offer_id: offer.id })),
        merchant_brand: brand.trim() || undefined,
        merchant_country: country.trim(),
        merchant_currency: currency.trim(),
        source: 'merchant_inbound',
      });
      toaster.success({ title: t('toast.dealCreated') });
      onCreated();
      onClose();
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ?? t('errors.deals.actionFailed'))
          : t('errors.deals.actionFailed'),
      });
    }
  };

  return (
    <Dialog
      title={t('pages.merchant.apply.title')}
      width={640}
      onClose={onClose}>
      <div className="space-y-4">
        <div>
          <Label htmlFor="apply-brand">{t('pages.deals.fields.brand')}</Label>
          <Input
            id="apply-brand"
            value={brand}
            onChange={(event) => setBrand(event.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor="apply-country">
              {t('pages.deals.fields.country')}
            </Label>
            <Input
              id="apply-country"
              value={country}
              onChange={(event) => setCountry(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="apply-currency">
              {t('pages.deals.fields.currency')}
            </Label>
            <Input
              id="apply-currency"
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
            />
          </div>
        </div>
        <div className="rounded-xl bg-muted p-4">
          <p className="text-sm font-medium">
            {t('pages.merchant.apply.selected')}
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
            {offers.map((offer) => (
              <li key={offer.id}>{getOfferDisplayName(offer)}</li>
            ))}
          </ul>
        </div>
      </div>
      <DialogFooter
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.cancel')}
            </Button>
            <Button
              disabled={
                mutation.isPending || !country.trim() || !currency.trim()
              }
              type="button"
              variant="brand"
              onClick={() => void submit()}>
              {t('buttons.submit')}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

MerchantApply.displayName = 'MerchantApply';
